/* eslint-disable react-hooks/exhaustive-deps */
import { HSSelect, HSText } from '@/Components/Common/HSWidget';
import { HSModal } from '@/Components/Modals/HSModal';
import { useMainContext } from '@/MainContext';
import { APP_REGISTRY_LAUNCHER_PLACEMENT } from '@/constants/appRegistry';
import { SESSION_STORAGE_KEYS } from '@/constants/device';
import { PlusOutlined, UserOutlined } from '@ant-design/icons';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { updatePassword } from '@vantiva-sa/homesight_humaui/Services/KeycloakService';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { getProfilePics, getUserInfo } from '@vantiva-sa/homesight_humaui/Store/dataStore';
import {
  formatFullNameDisplay,
  getProfilePicUrl,
  isManage,
} from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import {
  DefaultUnit,
  PhotoResolution,
  Units,
} from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import SessionStorage from '@vantiva-sa/homesight_humaui/Utilities/SessionStorage';
import {
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  Slider,
  Spin,
  Switch,
  Tabs,
  Tooltip,
  Upload,
} from 'antd';
import TabPane from 'antd/es/tabs/TabPane';
import dayjs from 'dayjs';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import MainPage from '../../MainPage';
import CustomDialogBox from '../Common/CustomDailogBox';
import {
  DashboardIcon,
  DeleteIcon,
  DOBCalendarIcon,
  EditIcon,
  EditNewIcon,
  ErrorIcon,
  MapIcon,
  NewCloseIcon,
  NewGearIcon,
  NewSaveIcon,
  NewUpdateIcon,
} from '../Common/Icons';
import Loading from '../Common/Loading';
import NewCustomButton, { ButtonTypes } from '../Common/NewCustomButton';
import { isTimeZoneValid } from '../Common/TimezoneUtils';
import { hasStaffPrivileges } from '../DeviceInventory/helpers';
import useServiceProvider from '../Hooks/useServiceProvider';
import useUserInfo from '../Hooks/useUserInfo';
import AppRegistryList from './AppRegistryList';
import './EditUserProfile.less';

/**
 * This component uses "autoacceptcall" (array of roles) plus "revokeautocallroles" (array of roles).
 * Example final payload structure for auto-call toggles:
 * {
 *   autoacceptcall: [ "Care"],      // the user specifically allows these roles
 *   revokeautocallroles: ["Manage", "Social"],  // the user disallows these roles
 * }
 */

const MODAL_TYPE = { DID_YOU_KNOW: 'did-you-know', UNSAVED_APP_CHANGES: 'unsaved-app-changes' };
const initialNotificationState = { desktop: false, mobile: false, email: false, textSms: false };

const EditUserProfile = ({
  parentBread,
  hubRecord,
  fetchUserHubs,
  onActionClick,
  handleTabChange,
  userHubCall = false,
  openSubMenu = false,
  showContactingStaff,
  successCallBack = () => {},
  parentActionClick,
  showStaffAccessBlocks = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname.split('/')[1]?.toLowerCase();
  const [form] = Form.useForm();

  // Access APIs, translation, current user
  const trans = useSelector(translator);
  const { setNavigationGuard } = useMainContext();
  const userApi = useSelector((state) => state.apis.userApi);
  const currentUser = useSelector(getCurrentUser);
  const providerid = useMemo(() => currentUser?.providerid, [currentUser]);
  const [providerData] = useServiceProvider({ providerid, forceFetch: false });
  const currentUserInfo = useSelector(getUserInfo(currentUser?.useruuid));

  // Figure out which user's data we're editing
  const useruuid = useMemo(
    () => (hubRecord ? hubRecord.useruuid : currentUser.useruuid),
    [hubRecord, currentUser]
  );

  // permission for delete account button
  const allowDeletePermission = useMemo(() => {
    if (openSubMenu && currentUser?.permission_level === 'Staff Admin') {
      return true;
    } else {
      return false;
    }
  }, [openSubMenu, currentUser]);

  // Only staff/staff-admin with Manage-level access to this hub can toggle app-registry apps on/off.
  const canManageApps = useMemo(
    () => hasStaffPrivileges(currentUser?.permission_level) && isManage(hubRecord),
    [currentUser, hubRecord]
  );

  // Account user name
  const accountantName = useMemo(
    () => formatFullNameDisplay(hubRecord?.firstname, hubRecord?.lastname),
    [hubRecord?.firstname, hubRecord?.lastname]
  );

  // Fetch user info
  const {
    data: userInfo = {},
    isFetching: fetchingUserInfo,
    refetch: fetchData,
  } = useUserInfo({ useruuid });
  const isLoggedInUser = currentUser.useruuid === userInfo.useruuid;

  // For toggles that mirror staffcontact
  const allowedPermissions = 4 - (userInfo?.revoke?.length || 0);
  const toggleUpdateOnToggleAllow = allowedPermissions === 0 || allowedPermissions === 4;

  // Basic UI states
  const [areButtonsDisabled, setAreButtonsDisabled] = useState(true);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isUpdatingData, setIsUpdatingData] = useState(false);
  const [showWarningmessage, setShowWarningMessage] = useState(false);

  // Profile photo
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotourl, setProfilePhotoUrl] = useState('');
  const [isProfilePhotoUpdated, setIsProfilePhotoUpdated] = useState(false);

  // 24-hour format
  const [isTimeFormatEnabled, setIsTimeFormatEnabled] = useState(false);

  const [autoCallAllow, setAutoCallAllow] = useState(false);
  const [autoCallManage, setAutoCallManage] = useState(false);
  const [autoCallCare, setAutoCallCare] = useState(false);
  const [autoCallSocial, setAutoCallSocial] = useState(false);

  // Remember initial states for auto-call toggles
  const [initialAutoCallAllow, setInitialAutoCallAllow] = useState(false);
  const [initialAutoCallManage, setInitialAutoCallManage] = useState(false);
  const [initialAutoCallCare, setInitialAutoCallCare] = useState(false);
  const [initialAutoCallSocial, setInitialAutoCallSocial] = useState(false);

  const [allow, setAllow] = useState(true);
  const [accountPrincipal, setAccountPrincipal] = useState(false);
  const [manage, setManage] = useState(true);
  const [care, setCare] = useState(true);
  const [social, setSocial] = useState(true);

  // Remember initial staffContact states
  const [initialAllow, setInitialAllow] = useState(false);
  const [initialAccountPrincipal, setInitialAccountPrincipal] = useState(false);
  const [initialManage, setInitialManage] = useState(false);
  const [initialCare, setInitialCare] = useState(false);
  const [initialSocial, setInitialSocial] = useState(false);

  // For Delete Account managing dialog and api call
  // Single, centrally-managed dialog shared across this component.
  const [dialogConfig, setDialogConfig] = useState(null);
  const [processing, setProcessing] = useState(false);

  const closeDialog = useCallback(() => setDialogConfig(null), []);
  const openDialog = useCallback((config) => setDialogConfig(config), []);

  //Smart camera configuration states
  const [volumeLevel, setVolumeLevel] = useState(null);
  const [autoWakeup, setAutoWakeup] = useState(false);
  const [autoSwitchNtf, setAutoSwitchNtf] = useState(false);
  // To track initial values for comparison
  const [initialAutoWakeup, setInitialAutoWakeup] = useState(false);
  const [initialAutoSwitchNtf, setInitialAutoSwitchNtf] = useState(false);
  /* for deployment status */
  const [isDeployed, setIsDeployed] = useState(true);
  // For hide video controls
  const [showVideoControls, setShowVideoControls] = useState(true);
  // For audio call only toggle
  const [audioCallOnly, setAudioCallOnly] = useState(false);
  //Screensaver and Text to speech states
  const [allowScreenSaver, setAllowScreenSaver] = useState(false);
  const [allowTextToSpeech, setAllowTextToSpeech] = useState(false);
  // To track initial values for comparison
  const [initialScreenSaver, setInitialScreenSaver] = useState(false);
  const [initialTextToSpeech, setInitialTextToSpeech] = useState(false);
  const [selectedScreenSaverDelay, setSelectedScreenSaverDelay] = useState([
    { screenSaverKey: 'screensaver-delay', itemKey: 15 },
  ]);
  const [selectedNotificationTimeoutDelay, setSelectedNotificationTimeoutDelay] = useState([
    { notificationTimeoutKey: 'notification-timeout', itemKey: 0 },
  ]);
  // For user personal information handling tabs
  const [activeUserInfo, setActiveUserInfo] = useState('1');
  // Only one of Channels/Apps AppRegistryList is ever mounted at a time (Tabs uses
  // destroyOnHidden), so a single ref always points at whichever one is currently active.
  const appRegistryListRef = useRef(null);
  // Action to run once the unsaved-changes dialog is confirmed - either switching this
  // page's own tab, or completing a sidebar navigation the user attempted.
  const [pendingUnsavedAction, setPendingUnsavedAction] = useState(null);
  const [notificationAlertSettings, setNotificationAlertSettings] =
    useState(initialNotificationState);
  const [initialNotificationAlertSettings, setInitialNotificationAlertSettings] =
    useState(initialNotificationState);

  const [hsModalDetails, setHSModalDetails] = useState({
    open: false,
    title: '',
    content: '',
    footer: '',
    type: '',
  });

  const canShowAuthSettings = useMemo(
    () => (showStaffAccessBlocks && hubRecord?.rolename !== undefined) || showContactingStaff,
    [hubRecord?.rolename, showContactingStaff, showStaffAccessBlocks]
  );

  const genderOptions = [
    { value: 'Male', label: trans('MALE') },
    { value: 'Female', label: trans('FEMALE') },
    { value: 'Indeterminate', label: trans('INDETERMINATE') },
    { value: 'Not Known', label: trans('NOTKNOWN') },
  ];

  const screenSaverMenuItems = [
    {
      key: 'screensaver',
      children: [
        { key: 15, label: trans('AFTER15MINUTES') },
        { key: 10, label: trans('AFTER10MINUTES') },
        { key: 5, label: trans('AFTER5MINUTES') },
      ],
    },
  ];

  const notificationTimeoutOptions = (key) => {
    if (key === 0) return trans('NEVER');

    if (key >= 60) {
      const hours = Math.floor(key / 60);
      return `${trans('AFTER')} ${hours} ${trans(hours === 1 ? 'HOUR' : 'HOURS')}`;
    }

    return `${trans('AFTER')} ${key} ${trans(key === 1 ? 'MINUTE' : 'MINUTES')}`;
  };

  const getNotificationTimeoutChildren = (str) => {
    return str.split(',').map((num) => {
      const key = Number(num.trim());
      return {
        key,
        label: notificationTimeoutOptions(key),
      };
    });
  };

  const notificationTimeoutMenuItems = [
    {
      key: 'notification-timeout',
      children: getNotificationTimeoutChildren(
        userInfo?.config?.supported_notification_interval || ''
      ),
    },
  ];

  const toSelectOptions = (menuItems) =>
    (menuItems?.[0]?.children || []).map(({ key, label }) => ({ value: key, label }));

  const handleScreenSaverChange = (value) => {
    setSelectedScreenSaverDelay([{ screenSaverKey: 'screensaver-delay', itemKey: Number(value) }]);
  };

  const handleNotificationTimeoutChange = (value) => {
    setSelectedNotificationTimeoutDelay([
      { notificationTimeoutKey: 'notification-timeout', itemKey: Number(value) },
    ]);
  };

  const getScreenSaverDelayValue = () => {
    const screenSaverDelayOptions = screenSaverMenuItems?.[0]?.children || [];
    const ScreenSaverTimeoutFromUserConfig = Number(
      selectedScreenSaverDelay?.[0]?.itemKey ?? userInfo?.config?.screensaver_idle_timeout
    );
    const isScreenSaverTimeoutOptionValid = screenSaverDelayOptions.find(
      (o) => Number(o.key) === ScreenSaverTimeoutFromUserConfig
    );
    const ScreenSaverFallback = screenSaverDelayOptions.find((o) => Number(o.key) === 15);
    return (isScreenSaverTimeoutOptionValid || ScreenSaverFallback)?.key;
  };

  const getNotificationTimeoutValue = () => {
    const notificationTimeoutOptions = notificationTimeoutMenuItems?.[0]?.children || [];
    const NotificationTimeoutFromUserConfig = Number(
      selectedNotificationTimeoutDelay?.[0]?.itemKey ??
        userInfo?.config?.display_notification_interval
    );
    const isNotificationTimeoutOptionValid = notificationTimeoutOptions.find(
      (o) => Number(o.key) === NotificationTimeoutFromUserConfig
    );
    const NotificationTimeoutFallback = notificationTimeoutOptions.find((o) => Number(o.key) === 0);
    return (isNotificationTimeoutOptionValid || NotificationTimeoutFallback)?.key;
  };

  // Runs `proceed` immediately if there are no unsaved Channels/Apps toggle changes, or
  // defers it behind the unsaved-changes confirmation dialog otherwise. Returns true if it
  // was deferred (caller should not also run its own fallback), false if it ran immediately.
  const guardAgainstUnsavedAppChanges = useCallback(
    (proceed) => {
      if (!appRegistryListRef.current?.hasPendingChanges?.()) {
        return false;
      }
      setPendingUnsavedAction(() => proceed);
      setHSModalDetails((prev) => ({
        ...prev,
        open: true,
        type: MODAL_TYPE.UNSAVED_APP_CHANGES,
        title: <HSText className="title-blue">{trans('UNSAVEDCHANGES')}</HSText>,
        content: <HSText className="body">{trans('AREYOUSUREYOUWANTTODISCRADCHANGES?')}</HSText>,
      }));
      return true;
    },
    [trans]
  );

  // MainSider calls navigationGuard(proceed) and trusts proceed() gets called eventually -
  // it doesn't check a return value or have its own fallback, so this wrapper always
  // resolves proceed(), either immediately or after the dialog is confirmed.
  const registerNavigationGuard = useCallback(
    (proceed) => {
      if (!guardAgainstUnsavedAppChanges(proceed)) {
        proceed();
      }
    },
    [guardAgainstUnsavedAppChanges]
  );

  useEffect(() => {
    setNavigationGuard(() => registerNavigationGuard);
    return () => setNavigationGuard(null);
  }, [registerNavigationGuard, setNavigationGuard]);

  const changeTabForUserInfo = (key) => {
    if (guardAgainstUnsavedAppChanges(() => setActiveUserInfo(key))) {
      return;
    }
    setActiveUserInfo(key); // update local state
  };

  const toggleNotificationAlertSetting = (key) => {
    const toggle = () =>
      setNotificationAlertSettings((prev) => ({
        ...prev,
        [key]: !prev[key],
      }));

    const currentSetting = notificationAlertSettings[key];
    if (currentSetting) {
      toggle();
    } else {
      let description = '';
      switch (key) {
        case 'desktop': {
          description = trans('YOUWILLRECEIVEDESKTOPNOTIFICATIONS');
          break;
        }
        case 'mobile': {
          description = trans('YOUWILLRECEIVEMOBILENOTIFICATIONS');
          break;
        }
        case 'email': {
          description = trans('YOUWILLRECEIVEEMAILNOTIFICATIONS');
          break;
        }
        case 'textSms': {
          description = trans('YOUWILLRECEIVETEXTSMSNOTIFICATIONS');
          break;
        }
      }
      openDialog({
        title: trans('AREYOUSURE'),
        description,
        confirmLabel: trans('ACCEPT'),
        confirmIcon: <NewSaveIcon className="delete-icon" />,
        cancelLabel: trans('DENY'),
        onConfirm: toggle,
      });
    }
  };

  const getInsightsAlertSettingsFromConfig = (config = {}) => ({
    desktop: Boolean(config?.insights_alert_desktop),
    email: Boolean(config?.insights_alert_email),
    mobile: Boolean(config?.insights_alert_mobile_push),
    textSms: Boolean(config?.insights_alert_text_sms),
  });

  const deleteAccount = () => {
    setProcessing(false);
    const errorCallback = (error) => {
      setProcessing(false);
      // message.error("Error deleting Account!");
      console.error('Error deleting Account:', error);
    };
    const successCallback = (data) => {
      setProcessing(false);
      // message.success("Account deleted successfully!");
      parentActionClick(null, null, false, true)();
    };
    try {
      setProcessing(true);
      userApi.removeUserById(
        hubRecord?.useruuid,
        apiCallback({
          failCallback: errorCallback,
          errorCallback,
          successCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  const handleDeleteButtonClick = () => {
    openDialog({
      title: trans('DELETEACCOUNT'),
      userFullName: accountantName,
      onConfirm: deleteAccount,
    });
  };

  // Normal form data
  const [formData, setFormData] = useState({});
  const formRef = useRef();
  const postalCodeRef = useRef(null);

  // For images
  const resourcesMap = useSelector(getProfilePics());
  const photoUrl = getProfilePicUrl(useruuid, resourcesMap, PhotoResolution.R256);

  // Default fallback locale/timezone
  const defaultLocaleVal = providerData?.supportedlocales?.[0]?.slice(-6, -1) || 'en-US';
  const defaultTimezoneVal =
    providerData?.supportedtimezones?.find((tz) => tz[defaultLocaleVal])?.[defaultLocaleVal] ||
    'America/Los_Angeles (PDT)';

  // BreadCrumb logic
  const createSearchParams = () => {
    const currentSearchParams = new URLSearchParams(location.search);
    const paramsMap = new Map(currentSearchParams.entries());
    const uniqueSearchParams = new URLSearchParams(paramsMap);
    return uniqueSearchParams.toString();
  };

  // Tab change
  const changeTabKey = (key) => {
    handleTabChange(key);
  };

  // Once we have userInfo, fill form fields & toggles
  useEffect(() => {
    const obj = {
      firstName: userInfo?.firstname ?? '',
      lastName: userInfo?.lastname ?? '',
      timeZone: userInfo?.timezone ?? defaultTimezoneVal[0] ?? '',
      units: userInfo?.measurement ?? DefaultUnit.value ?? '',
      latitude: userInfo?.latitude ?? '',
      longitude: userInfo?.longitude ?? '',
      postalCode: userInfo?.postalcode ?? '',
      email: userInfo?.email ?? '',
      staffRole: userInfo?.staffrole ?? '',
      locale: userInfo?.locale ?? defaultLocaleVal,
      is24hoursformat: userInfo?.is24hoursformat ?? false,
      gender: userInfo?.gender ?? '',
      address: userInfo?.address ?? '',
      province: userInfo?.province ?? '',
      country: userInfo?.country ?? '',
      dateofbirth: userInfo?.dateofbirth ? dayjs(userInfo.dateofbirth, 'DD/MM/YYYY') : null,
      building: userInfo?.building ?? '',
      city: userInfo?.city ?? '',
      deployment_state: userInfo?.deployment_state ?? 'NOT_DEPLOYED',
    };

    setFormData(obj);
    form.setFieldsValue(obj);
    setProfilePhoto(userInfo.photo);
    setProfilePhotoUrl(photoUrl);
    setVolumeLevel(userInfo?.config?.volume_level ?? 7);
    const isAllowEnabled = (userInfo?.revoke?.length || 0) < 4;
    setInitialAllow(isAllowEnabled);
    setAllow(isAllowEnabled);

    const isAccountPrincipal = !userInfo?.revoke?.includes('Principal');
    const isManageEnabled = !userInfo?.revoke?.includes('Manage');
    const isCareEnabled = !userInfo?.revoke?.includes('Care');
    const isSocialEnabled = !userInfo?.revoke?.includes('Social');

    setInitialAccountPrincipal(isAccountPrincipal);
    setInitialManage(isManageEnabled);
    setInitialCare(isCareEnabled);
    setInitialSocial(isSocialEnabled);

    setAccountPrincipal(isAccountPrincipal);
    setManage(isManageEnabled);
    setCare(isCareEnabled);
    setSocial(isSocialEnabled);

    // 24-hr format
    setIsTimeFormatEnabled(userInfo?.is24hoursformat ?? false);

    // auto-call roles (from userInfo.allowautocallroles, if present)
    const autoCallRolesArray = userInfo?.allowautocallroles || [];
    const autoCallRoles = autoCallRolesArray.filter((item) => item !== 'Principal');

    const hasAnyAutoCallRole = autoCallRoles.length > 0;

    setAutoCallAllow(hasAnyAutoCallRole);
    setInitialAutoCallAllow(hasAnyAutoCallRole);

    // Removed autoCallPrincipal; only update the remaining roles:
    setAutoCallManage(autoCallRoles.includes('Manage'));
    setAutoCallCare(autoCallRoles.includes('Care'));
    setAutoCallSocial(autoCallRoles.includes('Social'));
    setInitialAutoCallManage(autoCallRoles.includes('Manage'));

    setInitialAutoCallCare(autoCallRoles.includes('Care'));
    setInitialAutoCallSocial(autoCallRoles.includes('Social'));
    if (!userInfo?.modified_time) {
      setAreButtonsDisabled(false);
    } else {
      setAreButtonsDisabled(true);
    }
    setAutoWakeup(userInfo?.config?.auto_wakeup ?? false);
    setAutoSwitchNtf(userInfo?.config?.auto_switch_ntf ?? false);
    setInitialAutoWakeup(userInfo?.config?.auto_wakeup ?? false);
    setInitialAutoSwitchNtf(userInfo?.config?.auto_switch_ntf ?? false);
    const insightsAlertSettings = getInsightsAlertSettingsFromConfig(userInfo?.config);
    setNotificationAlertSettings(insightsAlertSettings);
    setInitialNotificationAlertSettings(insightsAlertSettings);
    setAllowScreenSaver(userInfo?.config?.screensaver_enable ?? false);
    setAllowTextToSpeech(userInfo?.config?.text_to_speech_enable ?? false);
    setInitialScreenSaver(userInfo?.config?.screensaver_enable ?? false);
    setShowVideoControls(
      userInfo?.config?.hide_tv_controls ? !userInfo?.config?.hide_tv_controls : true
    );
    setAudioCallOnly(userInfo?.config?.default_disable_video ?? false);
    setIsDeployed(userInfo?.deployment_state === 'DEPLOYED');
    if (userInfo?.config?.screensaver_idle_timeout != null) {
      const screenSaverDelayOptions = screenSaverMenuItems?.[0]?.children || [];
      const ScreenSaverTimeoutFromUserConfig = Number(userInfo?.config?.screensaver_idle_timeout);
      const isScreenSaverTimeoutOptionValid = screenSaverDelayOptions.some(
        (o) => Number(o.key) === ScreenSaverTimeoutFromUserConfig
      );
      const selectedScreenSaverTimeoutValue = isScreenSaverTimeoutOptionValid
        ? ScreenSaverTimeoutFromUserConfig
        : 15;
      setSelectedScreenSaverDelay([
        {
          screenSaverKey: 'screensaver-delay',
          itemKey: selectedScreenSaverTimeoutValue,
        },
      ]);
    }
    if (userInfo?.config?.display_notification_interval != null) {
      const notificationTimeoutOptions = notificationTimeoutMenuItems?.[0]?.children || [];
      const NotificationTimeoutFromUserConfig = Number(
        userInfo?.config?.display_notification_interval
      );
      const isNotificationTimeoutOptionValid = notificationTimeoutOptions.some(
        (o) => Number(o.key) === NotificationTimeoutFromUserConfig
      );
      const selectedNotificationTimeoutValue = isNotificationTimeoutOptionValid
        ? NotificationTimeoutFromUserConfig
        : 0;
      setSelectedNotificationTimeoutDelay([
        {
          notificationTimeoutKey: 'notification-timeout',
          itemKey: selectedNotificationTimeoutValue,
        },
      ]);
    }
    setInitialTextToSpeech(userInfo?.config?.text_to_speech_enable ?? false);
  }, [userInfo, photoUrl, form, defaultLocaleVal, defaultTimezoneVal]);

  /* handling update button enabling using formchange */
  useEffect(() => {
    onFormChangeHandler();
  }, [
    autoCallManage,
    autoCallSocial,
    autoCallCare,
    autoCallAllow,
    allow,
    accountPrincipal,
    manage,
    care,
    social,
    isTimeFormatEnabled,
    isDeployed,
    showVideoControls,
    audioCallOnly,
    notificationAlertSettings.desktop,
    notificationAlertSettings.email,
    notificationAlertSettings.mobile,
    notificationAlertSettings.textSms,
  ]);

  // Manage locale in a local state
  const localeValue = useMemo(() => {
    // If userInfo locale is supported, use it; else fallback
    if (
      providerData?.supportedlocales &&
      providerData.supportedlocales.some((loc) => loc.includes(userInfo?.locale))
    ) {
      return userInfo?.locale;
    } else {
      return defaultLocaleVal;
    }
  }, [userInfo?.locale, providerData, defaultLocaleVal]);

  const [locale, setLocale] = useState(localeValue);

  // If user navigates to the "profile" route, show the breadCrumb differently
  let header = [];
  if (path.includes('profile')) {
    header = (parentBread || []).concat([
      {
        label: '',
        icon: (
          <Link to="/">
            <DashboardIcon />
          </Link>
        ),
      },
      {
        label: trans('EDITPROFILE'),
        icon: <UserOutlined />,
      },
    ]);
  } else {
    header = (parentBread || []).concat([
      {
        label: trans('EDITPROFILE'),
        icon: <UserOutlined />,
      },
    ]);
  }

  // For updating the user’s profile photo in a separate call
  const onProfilePhotoUpdate = (photo) => {
    const errorCallback = (error) => {
      setIsUpdatingProfile(false);
      setIsProfilePhotoUpdated(false);
      console.error('Error uploading picture:', error);
      // message.error("Error uploading picture");
      fetchData();
    };
    const successCallback = (data) => {
      setIsUpdatingProfile(false);
      setAreButtonsDisabled(true);
      setIsProfilePhotoUpdated(false);
      // message.success("Profile picture updated successfully");
      fetchData();
    };
    try {
      const payload = photo ? { profileimage: photo } : { profileimage: '' };
      userApi.upload(
        useruuid,
        payload,
        apiCallback({
          translator: trans,
          failCallback: errorCallback,
          errorCallback,
          successCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  const showModalConfirmation = () => {
    setHSModalDetails({
      ...hsModalDetails,
      open: true,
      type: MODAL_TYPE.DID_YOU_KNOW,
      title: <HSText className="title-blue">{trans('DIDYOUKNOW')}</HSText>,
      content: (
        <>
          <Row>
            <HSText className="form-title">{trans('POSTALCODEINOPTIONAL')}</HSText>
          </Row>
          <Row>
            <HSText className="body">{trans('CUSTOMEREXPERIENCE')}</HSText>
          </Row>
        </>
      ),
    });
  };

  const submitForm = () => {
    const errorCallback = (error) => {
      setIsUpdatingProfile(false);
      console.error('Error updating profile details:', error);
      // message.error("Error updating profile details");
    };

    const successCallback = (data) => {
      if (isProfilePhotoUpdated) {
        onProfilePhotoUpdate(profilePhoto);
      } else {
        fetchUserHubs && fetchUserHubs();
        setIsUpdatingProfile(false);
        setAreButtonsDisabled(true);
        fetchData();
        successCallBack();
      }
      setIsUpdatingData(false);
      // message.success("Profile details updated successfully");

      if (isLoggedInUser) {
        navigate('/' + '?' + createSearchParams());
      } else if (userHubCall) {
        onActionClick(null, null, openSubMenu, true)();
      } else {
        changeTabKey('schedule');
      }
    };

    // If we already updated the photo, or not
    if (isProfilePhotoUpdated && !isUpdatingData) {
      onProfilePhotoUpdate(profilePhoto);
    } else {
      form
        .validateFields([
          'firstName',
          'lastName',
          'postalCode',
          'timeZone',
          'units',
          'staffRole',
          'locale',
          'is24hoursformat',
          'gender',
          'address',
          'province',
          'country',
          'dateofbirth',
          'building',
          'city',
          'deployment_state',
        ])
        .then((values) => {
          try {
            setIsUpdatingProfile(true);

            // Basic user object
            const user = {
              useruuid: useruuid,
              firstname: values.firstName.trim(),
              lastname: values.lastName.trim(),
              postalcode: values.postalCode,
              timezone: values.timeZone,
              measurement: values.units,
              staffrole: values.staffRole,
              locale: values.locale,
              is24hoursformat: isTimeFormatEnabled,
              gender: values.gender,
              address: values.address,
              province: values.province,
              country: values.country,
              dateofbirth: values.dateofbirth?.format('DD/MM/YYYY'),
              building: values.building,
              city: values.city,
              deployment_state: isDeployed ? 'DEPLOYED' : 'NOT_DEPLOYED',
            };

            const allowList = [];
            const revokeList = [];

            if (allow) {
              if (accountPrincipal) allowList.push('Principal');
              if (manage) allowList.push('Manage');
              if (care) allowList.push('Care');
              if (social) allowList.push('Social');
            } else {
              // If main "Allow" is off for staffcontact
              revokeList.push('Principal', 'Manage', 'Care', 'Social');
            }
            // If allow is on but a subtoggle is off, revoke that role
            if (allow) {
              if (!accountPrincipal) revokeList.push('Principal');
              if (!manage) revokeList.push('Manage');
              if (!care) revokeList.push('Care');
              if (!social) revokeList.push('Social');
            }
            const autoCallArray = [];
            const revokeAutoCallArray = [];

            if (autoCallAllow) {
              if (autoCallManage) autoCallArray.push('Manage');
              if (autoCallCare) autoCallArray.push('Care');
              if (autoCallSocial) autoCallArray.push('Social');

              if (!autoCallManage) revokeAutoCallArray.push('Manage');
              if (!autoCallCare) revokeAutoCallArray.push('Care');
              if (!autoCallSocial) revokeAutoCallArray.push('Social');
            } else {
              revokeAutoCallArray.push('Manage', 'Care', 'Social');
            }

            const payload = {
              ...user,
              // staffcontact
              allow: allowList,
              revoke: revokeList,
              allowautocallroles: autoCallArray,
              revokeautocallroles: revokeAutoCallArray,
              config: {
                ...userInfo.config,
                volume_level: volumeLevel,
                auto_wakeup: autoWakeup,
                auto_switch_ntf: autoSwitchNtf,
                screensaver_enable: allowScreenSaver,
                text_to_speech_enable: allowTextToSpeech,
                insights_alert_desktop: notificationAlertSettings.desktop,
                insights_alert_email: notificationAlertSettings.email,
                insights_alert_mobile_push: notificationAlertSettings.mobile,
                insights_alert_text_sms: notificationAlertSettings.textSms,
                hide_tv_controls: !showVideoControls,
                default_disable_video: audioCallOnly,
                screensaver_idle_timeout: Number(selectedScreenSaverDelay?.[0]?.itemKey ?? 15),
                display_notification_interval: Number(
                  selectedNotificationTimeoutDelay?.[0]?.itemKey ?? 0
                ),
              },
            };

            userApi.updateUser(
              payload,
              apiCallback({
                translator: trans,
                failCallback: errorCallback,
                errorCallback,
                successCallback,
              })
            );
          } catch (error) {
            errorCallback(error);
          }
        })
        .catch(() => {
          // message.error("Please fill in all required fields.");
          console.error('Please fill in all required fields.');
        });
    }
  };

  const onUpdateHandler = () => {
    const postalCode = form.getFieldValue('postalCode');
    if (!postalCode) {
      showModalConfirmation();
      return;
    }
    submitForm();
  };

  // Handling file upload for the avatar
  const handleUploadChange = (info) => {
    if (info.file.status === 'uploading') {
      return;
    }
    if (info.file.status === 'done') {
      const file = info.file.originFileObj;
      if (file) {
        if (profilePhotourl && profilePhotourl.startsWith('blob:')) {
          URL.revokeObjectURL(profilePhotourl);
        }

        const blobUrl = URL.createObjectURL(file);
        setIsProfilePhotoUpdated(true);
        setAreButtonsDisabled(false);
        setProfilePhoto(file);
        setProfilePhotoUrl(blobUrl);
      }
    }
  };

  useEffect(() => {
    return () => {
      if (profilePhotourl && profilePhotourl.startsWith('blob:')) {
        URL.revokeObjectURL(profilePhotourl);
      }
    };
  }, [profilePhotourl]);

  // Cancel = revert all changes
  const onCancelHandler = () => {
    form.resetFields();
    setProfilePhoto(userInfo.photo);
    setProfilePhotoUrl(photoUrl);
    setAreButtonsDisabled(true);
    setAutoWakeup(initialAutoWakeup);
    setAutoSwitchNtf(initialAutoSwitchNtf);
    setAllowScreenSaver(initialScreenSaver);
    setAllowTextToSpeech(initialTextToSpeech);
    setSelectedScreenSaverDelay([
      {
        screenSaverKey: 'screensaver-delay',
        itemKey: Number(userInfo?.config?.screensaver_idle_timeout ?? 15),
      },
    ]);
    setSelectedNotificationTimeoutDelay([
      {
        notificationTimeoutKey: 'notification-timeout',
        itemKey: Number(userInfo?.config?.display_notification_interval ?? 0),
      },
    ]);
    setNotificationAlertSettings(initialNotificationAlertSettings);

    if (isLoggedInUser) {
      navigate('/' + '?' + createSearchParams());
    } else if (userHubCall) {
      onActionClick(null, null, openSubMenu, true)();
    } else {
      changeTabKey('schedule');
    }
  };

  // For "Change Password"
  const onChangePasswordHandler = () => {
    updatePassword();
  };

  // Render the top heading
  const renderHeaderSection = () => {
    return (
      <div className="edit-profile-header">
        <div className="edit-profile-title-container">
          {canShowAuthSettings ? (
            <EditNewIcon
              className="edit-icon"
              style={{ width: '36px', height: '36px', fill: '#f5f5f5' }}
            />
          ) : (
            <NewGearIcon
              className="edit-icon"
              style={{ width: '36px', height: '36px', fill: '#f5f5f5' }}
            />
          )}
          <p>{trans('EDITPROFILE')}</p>
        </div>
      </div>
    );
  };

  //handled form manadtory text and buttons for submission
  const renderMandatoryTextAndButtons = ({ showMandatoryLabel = true } = {}) => {
    return (
      <>
        {showMandatoryLabel && (
          <div className="staffsaccount-access-mandatory-label">
            {trans('*THESEFIELDSAREMANDATORY')}
          </div>
        )}
        <div
          className={`${
            hubRecord === undefined
              ? 'edit-profile-button-container-new'
              : 'edit-profile-button-container-new edit-contact-staff'
          } `}
        >
          {allowDeletePermission && (
            <div className="form-button">
              <NewCustomButton
                className={'delete-form'}
                label={trans('DELETEACCOUNT')}
                icon={<DeleteIcon className="delete-icon" />}
                onClick={handleDeleteButtonClick}
              />
            </div>
          )}
          <div className="form-button">
            <NewCustomButton
              className="cancel"
              onClick={onCancelHandler}
              label={trans('CANCEL')}
              icon={<NewCloseIcon className="cancel-icon" />}
            />
          </div>

          <div className="form-button">
            <NewCustomButton
              type={ButtonTypes.primary}
              label={trans('UPDATE')}
              icon={
                <NewUpdateIcon
                  className={!areButtonsDisabled ? 'update-icon-enabled' : 'update-icon-disabled'}
                />
              }
              onClick={onUpdateHandler}
              disabled={areButtonsDisabled}
            />
          </div>
        </div>
      </>
    );
  };

  const renderProfileAvatar = () => {
    return (
      <>
        <Upload
          onChange={handleUploadChange}
          showUploadList={false}
          customRequest={({ file, onSuccess }) => {
            setTimeout(() => {
              onSuccess('ok');
            }, 0);
          }}
          accept=".png,.jpg,.jpeg,.gif,.jfif,.webp,.svg,.tiff"
        >
          {profilePhotourl ? (
            <div className="profile-avatar-container">
              <img
                className={`profile-avatar ${profilePhotourl ? 'profile-avatar-border' : ''}`}
                src={profilePhotourl}
                alt="Avatar"
              />
              <div className="edit-profile-edit-container">
                <div className="upload-remove-picture upload">
                  <span className="label upload">
                    {' '}
                    <EditIcon className="edit-delete-icon" />
                    {trans('UPLOADPICTURE')}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="emptyprofile">
              <div className="empty-profile-container">
                <PlusOutlined />
              </div>
              {/* <img className="profile-pencil" src={Pencil} alt="Edit" /> */}
              <div className="edit-profile-edit-container">
                <div className="upload-remove-picture upload">
                  <span className="label upload">
                    <EditIcon className="edit-delete-icon" /> {trans('UPLOADPICTURE')}
                  </span>
                </div>
              </div>
            </div>
          )}
        </Upload>
        {/* {profilePhotourl && (
        <div className="profile-avatar-container">
                <div className="edit-profile-edit-container">
                  <div className="upload-remove-picture remove" onClick={() => {
                    setProfilePhoto(null);
                    setProfilePhotoUrl("");
                    setIsProfilePhotoUpdated(false);
                    setAreButtonsDisabled(false);
                    onProfilePhotoUpdate("")
                    }}>
                      <span className="label remove"> <DeleteIcon className="edit-delete-icon" />Remove Picture</span>
                  </div>
                </div>
          </div>
      )} */}
      </>
    );
  };

  // Called on ANY form field change; we decide if the Update button is active
  const onFormChangeHandler = () => {
    const currentValues = form.getFieldsValue();

    const trimmedFirstName = currentValues.firstName ? currentValues.firstName.trim() : '';
    const trimmedLastName = currentValues.lastName ? currentValues.lastName.trim() : '';

    const isFirstNameChanged = hasSignificantChange(currentValues.firstName, formData.firstName);
    const isLastNameChanged = hasSignificantChange(currentValues.lastName, formData.lastName);

    const isFirstNameEmpty = trimmedFirstName === '';
    const isLastNameEmpty = trimmedLastName === '';

    const isOtherFieldsChanged = Object.keys(currentValues).some((key) => {
      if (key === 'firstName' || key === 'lastName') return false;
      return currentValues[key] !== formData[key];
    });

    const timeZone = formRef.current ? formRef.current.getFieldValue('timeZone') : '';
    const locale = formRef.current ? formRef.current.getFieldValue('locale') : '';

    // staffcontact toggles
    const isAccountPrincipalChanged = accountPrincipal !== initialAccountPrincipal;
    const isManageChanged = manage !== initialManage;
    const isCareChanged = care !== initialCare;
    const isSocialChanged = social !== initialSocial;
    const isAllowChanged = allow !== initialAllow;
    const is24HourFormatChanged = isTimeFormatEnabled !== userInfo?.is24hoursformat;

    const isAutoCallAllowChanged = autoCallAllow !== initialAutoCallAllow;
    const isAutoCallManageChanged = autoCallManage !== initialAutoCallManage;
    const isAutoCallCareChanged = autoCallCare !== initialAutoCallCare;
    const isAutoCallSocialChanged = autoCallSocial !== initialAutoCallSocial;

    const isVolumeChanged =
      volumeLevel != null && volumeLevel !== (userInfo?.config?.volume_level ?? 7);
    const isDeploymentStateChanged = isDeployed !== (userInfo?.deployment_state === 'DEPLOYED');
    const isShowVideoControlsChanged =
      showVideoControls !== (userInfo?.config?.hide_tv_controls ?? true);
    const isAudioCallOnlyChanged =
      audioCallOnly !== (userInfo?.config?.default_disable_video ?? false);

    const isAutoWakeupChanged = autoWakeup !== initialAutoWakeup;
    const isAutoSwitchNtfChanged = autoSwitchNtf !== initialAutoSwitchNtf;
    const isScreenSaverChanged = allowScreenSaver !== initialScreenSaver;
    const isTextToSpeechChanged = allowTextToSpeech !== initialTextToSpeech;
    const isScreenSaverDelayChanged =
      Number(selectedScreenSaverDelay[0]?.itemKey) !==
      Number(userInfo?.config?.screensaver_idle_timeout ?? 15);
    const isNotificationTimeoutChanged =
      Number(selectedNotificationTimeoutDelay[0]?.itemKey) !==
      Number(userInfo?.config?.display_notification_interval ?? 0);
    const isNotificationAlertsChanged =
      notificationAlertSettings.desktop !== initialNotificationAlertSettings.desktop ||
      notificationAlertSettings.email !== initialNotificationAlertSettings.email ||
      notificationAlertSettings.mobile !== initialNotificationAlertSettings.mobile ||
      notificationAlertSettings.textSms !== initialNotificationAlertSettings.textSms;
    // Only check specific fields
    const personalInfofieldsToCheck = [
      'gender',
      'address',
      'building',
      'country',
      'city',
      'province',
      'postalCode',
    ];
    const normalize = (val) => (val ?? '').toString().trim();
    const isPersonalInfoFieldsChanged = personalInfofieldsToCheck.some((key) => {
      return normalize(currentValues[key]) !== normalize(formData[key]);
    });
    const currentDob = dayjs(currentValues.dateofbirth);
    const initialDob = dayjs(formData.dateofbirth);
    const isDateOfBirthChanged =
      currentDob.format('YYYY-MM-DD') !== initialDob.format('YYYY-MM-DD');
    const isUnitsChanged = currentValues.units !== formData.units;
    const isTimeZoneChanged = currentValues.timeZone !== formData.timeZone;
    const isLocaleChanged = currentValues.locale !== formData.locale;

    if (isLocaleChanged && currentValues.locale !== undefined && formData.locale !== undefined) {
      SessionStorage.remove(SESSION_STORAGE_KEYS.FILTER);
      SessionStorage.remove(SESSION_STORAGE_KEYS.INVENTORY_FILTER);
      SessionStorage.remove(SESSION_STORAGE_KEYS.STAFF_FILTER);
    }

    const changed =
      (isFirstNameChanged && !isFirstNameEmpty) ||
      (isLastNameChanged && !isLastNameEmpty) ||
      isOtherFieldsChanged ||
      isPersonalInfoFieldsChanged ||
      isAccountPrincipalChanged ||
      isManageChanged ||
      isCareChanged ||
      isSocialChanged ||
      isAllowChanged ||
      is24HourFormatChanged ||
      isAutoCallAllowChanged ||
      isAutoCallManageChanged ||
      isAutoCallCareChanged ||
      isAutoCallSocialChanged ||
      isVolumeChanged ||
      isDeploymentStateChanged ||
      isAutoWakeupChanged ||
      isAutoSwitchNtfChanged ||
      isDateOfBirthChanged ||
      isTimeZoneChanged ||
      isLocaleChanged ||
      isUnitsChanged ||
      isScreenSaverChanged ||
      isTextToSpeechChanged ||
      isScreenSaverDelayChanged ||
      isNotificationTimeoutChanged ||
      isNotificationAlertsChanged ||
      isShowVideoControlsChanged ||
      isAudioCallOnlyChanged;

    setAreButtonsDisabled(!changed || !timeZone || !locale);
    setIsUpdatingData(true);
  };

  const hasSignificantChange = (currentValue, initialValue) => {
    const trimmedCurrent = (currentValue ?? '').trim();
    const trimmedInitial = (initialValue ?? '').trim();
    return trimmedCurrent !== trimmedInitial;
  };

  // staff check
  const isStaff = (user) => {
    return user?.usertype?.toLowerCase() === 'staff';
  };
  const isStaffRolePresent = isStaff(userInfo);

  // Channels/Apps tabs only apply to accounts that own an hc200 device.
  const isPrincipalUser = userInfo?.usertype?.toLowerCase() === 'principal';

  // Timezone combos
  const getTimezoneOptions = () => {
    const localeVal = formRef.current ? formRef.current.getFieldValue('locale') : locale;
    const timezones = providerData?.supportedtimezones?.find((tz) => tz[localeVal]) || {};
    return (
      timezones[localeVal]
        ?.filter((tz) => isTimeZoneValid(tz.split(' ')[0]))
        .map((timeZone) => ({
          label: timeZone,
          value: timeZone,
        })) ?? []
    );
  };

  useEffect(() => {
    if (!userInfo) return;
    const hasVolume = volumeLevel !== null;
    const hasAutoWakeup = autoWakeup !== null;
    const hasAutoSwitchNtf = autoSwitchNtf !== null;
    const hasScreenSaver = allowScreenSaver !== null;
    const hasTextToSpeech = allowTextToSpeech !== null;
    const hasNotificationTimeout = userInfo?.config?.display_notification_interval !== null;
    if (
      hasVolume ||
      hasAutoWakeup ||
      hasAutoSwitchNtf ||
      hasScreenSaver ||
      hasTextToSpeech ||
      hasNotificationTimeout
    ) {
      //ensure update button is evaluated
      onFormChangeHandler();
    }
  }, [
    volumeLevel,
    autoWakeup,
    autoSwitchNtf,
    userInfo,
    allowScreenSaver,
    allowTextToSpeech,
    selectedScreenSaverDelay,
    selectedNotificationTimeoutDelay,
  ]);

  const renderGeneralTab = () => {
    return (
      <>
        <div className="edit-profile-photo-container">{renderProfileAvatar()}</div>
        {canShowAuthSettings ? (
          <span className="edit-profile-general-info-label">{trans('WHOSACCOUNTISTHIS?')}</span>
        ) : (
          <span className="edit-profile-general-info-label-new">
            {trans('GENERAL_INFORMATION_L')}
          </span>
        )}
        <div className="edit-profile-content-container">
          <div className="edit-profile-form-row">
            <span className="edit-profile-form-row-title generalinfo">{trans('FIRSTNAME*')}</span>
            <Form.Item name={'firstName'} className="edit-profile-form-row-input-box">
              <Input placeholder={trans('FIRSTNAME*')} />
            </Form.Item>
          </div>
          <div className="edit-profile-form-row">
            <span className="edit-profile-form-row-title generalinfo">{trans('LASTNAME*')}</span>
            <Form.Item name={'lastName'} className="edit-profile-form-row-input-box">
              <Input placeholder={trans('LASTNAME*')} />
            </Form.Item>
          </div>
        </div>
        {canShowAuthSettings ? (
          <>
            <div className="edit-profile-content-container">
              <div className="edit-profile-profile-form-row">
                <span className="edit-profile-form-row-title generalinfo">
                  {trans('DATAOFBIRTH')}
                  <span className="optional-label">({trans('OPTIONAL')})</span>
                </span>
                <Form.Item name={'dateofbirth'} className="edit-profile-form-row-input-box">
                  <DatePicker
                    suffixIcon={<DOBCalendarIcon />}
                    placeholder={trans('DD/MM/YYYY')}
                    allowClear={false}
                    renderExtraFooter={() => null}
                    showToday={false}
                    dropdownClassName="custom-dob-calendar"
                    className="dob-date-select"
                    value={form.getFieldValue('dateofbirth') || null}
                    format={moment.localeData(currentUserInfo?.locale).longDateFormat('L')}
                    onChange={(date) => {
                      form.setFieldsValue({ dateofbirth: date });
                      onFormChangeHandler(); // to enable update button if needed
                    }}
                    disabledDate={(current) => {
                      // Disable all future dates after today
                      return current && current > dayjs().endOf('day');
                    }}
                  />
                </Form.Item>
              </div>
              <div className="edit-profile-form-row">
                <span className="edit-profile-form-row-title generalinfo">
                  {trans('GENDER')}
                  <span className="optional-label">({trans('OPTIONAL')})</span>
                </span>
                <Form.Item name="gender" className="edit-profile-form-row-input-box">
                  <HSSelect
                    options={genderOptions}
                    aria-label={trans('GENDER')}
                    onChange={onFormChangeHandler}
                  />
                </Form.Item>
              </div>
            </div>
            <Divider className="edit-profile-divider" />
            <div>
              <span className="edit-profile-general-info-label">
                {trans('GENERAL_INFORMATION_L')}
              </span>
              <div className="edit-profile-content-container">
                <div className="edit-profile-form-row">
                  <span className="edit-profile-form-row-title generalinfo">
                    {trans('ADDRESS')}
                  </span>
                  <Form.Item name={'address'}>
                    <Input
                      placeholder={trans('ADDRESS')}
                      prefix={
                        <span
                          style={{ display: 'flex', alignItems: 'center' }}
                          className="edit-profile-form-row-input-box"
                        >
                          <MapIcon style={{ fontSize: '16px' }} />
                        </span>
                      }
                    />
                  </Form.Item>
                </div>
                <div className="edit-profile-form-row">
                  <span className="edit-profile-form-row-title generalinfo">
                    {trans('BUILDING/APARTMENT')}
                  </span>
                  <Form.Item name={'building'} className="edit-profile-form-row-input-box">
                    <Input placeholder={trans('BUILDING')} />
                  </Form.Item>
                </div>
              </div>
              <div className="edit-profile-content-container local-details">
                <div
                  className="edit-profile-form-row local-details-row"
                  style={{ marginTop: '1rem' }}
                >
                  <span className="edit-profile-form-row-title generalinfo">{trans('CITY')}</span>
                  <Form.Item name={'city'} className="edit-profile-form-row-input-box">
                    <Input placeholder={trans('CITY')} style={{ width: '246px' }} />
                  </Form.Item>
                  <div className="postal-code-info-container">
                    <span className="postal-code-info-message">
                      <ErrorIcon
                        className="error-icon"
                        fillColor="#3FA9F5"
                        style={{ width: '24px' }}
                      />
                      {trans('CITYORPOSTALCODETOPROVIDETHEWEATHERFORECAST')}
                    </span>
                  </div>
                </div>
                <div className="edit-profile-form-row local-details-row">
                  <span className="edit-profile-form-row-title generalinfo">
                    {trans('STATE/PROVINCE')}
                  </span>
                  <Form.Item name={'province'} className="edit-profile-form-row-input-box">
                    <Input placeholder={trans('PROVINCE')} style={{ width: '196px' }} />
                  </Form.Item>
                </div>
                <div className="edit-profile-form-row local-details-row">
                  <div>
                    <span className="edit-profile-form-row-title generalinfo">
                      {trans('POSTALCODE')}*
                    </span>
                  </div>
                  <Form.Item name={'postalCode'} className="edit-profile-form-row-input-box">
                    <Input placeholder={trans('POSTAL_CODE_L')} style={{ width: '154px' }} />
                  </Form.Item>
                </div>
                <div className="edit-profile-form-row local-details-row">
                  <span className="edit-profile-form-row-title generalinfo">
                    {trans('COUNTRY')}*
                  </span>
                  <Form.Item name={'country'} className="edit-profile-form-row-input-box">
                    <Input placeholder={trans('COUNTRY')} style={{ width: '288px' }} />
                  </Form.Item>
                </div>
              </div>
              <div className="edit-profile-content-container">
                <div className="edit-profile-form-row">
                  <span className="edit-profile-form-row-title generalinfo">
                    {trans('LANGUAGE')}
                  </span>
                  <Form.Item name={'locale'} className="edit-profile-form-row-input-box">
                    <HSSelect
                      onSelect={onFormChangeHandler}
                      value={defaultLocaleVal}
                      placeholder={trans('SELECT_LANGUAGE_L')}
                      options={
                        providerData &&
                        providerData.supportedlocales
                          ?.map((item) => ({
                            value: item.slice(-6, -1),
                            label: item,
                          }))
                          .sort((a, b) => a.label.localeCompare(b.label))
                      }
                    />
                  </Form.Item>
                </div>
                <div className="edit-profile-form-row">
                  <span className="edit-profile-form-row-title generalinfo">
                    {trans('MEASUREMENT_UNIT')}
                  </span>
                  <Form.Item name="units" className="edit-profile-form-row-input-box">
                    <HSSelect
                      onSelect={onFormChangeHandler}
                      dropdownStyle={{ padding: 1 }}
                      options={Units.map((unit) => ({
                        label: `${unit.label}`,
                        value: `${unit.value}`,
                        placeholder: trans('SELECT_MEASUREMENTUNIT_L'),
                      }))}
                    />
                  </Form.Item>
                </div>
                <div style={{ marginTop: showWarningmessage ? '4rem' : '3.9rem' }}>
                  <div className="edit-profile-form-row">
                    <span className="edit-profile-form-row-title generalinfo">
                      {trans('TIME*')}
                    </span>
                    <Form.Item
                      name="timeZone"
                      className={`edit-profile-form-row-input-box ${
                        showWarningmessage ? 'timezone-error-field' : ''
                      }`}
                      validateStatus={showWarningmessage ? 'error' : ''}
                      help={
                        showWarningmessage ? (
                          <div className="error-text">
                            <span className="error-message">
                              <ErrorIcon className="error-icon" fillColor="#F46363" />
                              <span>{trans('PLEASEUPDATETHETIMEZONESELECTION')}</span>
                            </span>
                          </div>
                        ) : null
                      }
                    >
                      <HSSelect
                        className={`form-item-dropdown custom-select ${
                          showWarningmessage ? 'error-select' : ''
                        }`}
                        onSelect={() => {
                          onFormChangeHandler();
                          setShowWarningMessage(false); // Hide warning when user selects a time zone
                        }}
                        dropdownStyle={{ padding: 1 }}
                        options={getTimezoneOptions()}
                      />
                    </Form.Item>
                  </div>
                  <div className="edit-profile-form-row">
                    <Form.Item name={'is24hoursformat'} className="edit-profile-form-row-input-box">
                      <div className="togglebutton">
                        <div>
                          <Switch
                            checked={isTimeFormatEnabled}
                            onChange={(checked) => {
                              setIsTimeFormatEnabled(checked);
                              // onFormChangeHandler(
                              //   !form.getFieldValue("timeZone") ||
                              //     (checked === userInfo?.is24hoursformat &&
                              //       allow === initialAllow  &&
                              //       accountPrincipal === initialAccountPrincipal &&
                              //       manage === initialManage &&
                              //       care === initialCare &&
                              //       social === initialSocial &&
                              //       autoCallAllow === initialAutoCallAllow &&
                              //       autoCallManage === initialAutoCallManage &&
                              //       autoCallCare === initialAutoCallCare &&
                              //       autoCallSocial === initialAutoCallSocial &&
                              //       Object.keys(formData).every(
                              //         (key) =>
                              //           form.getFieldValue(key) === formData[key]
                              //       ))
                              // );
                            }}
                          />
                        </div>
                        <div className="timeformat-text">{trans('24HOURFORMAT_L')}</div>
                      </div>
                    </Form.Item>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="edit-profile-content-container">
              <div className="edit-profile-form-row">
                <span className="edit-profile-form-row-title generalinfo">{trans('LANGUAGE')}</span>
                <Form.Item name={'locale'} className="edit-profile-form-row-input-box">
                  <HSSelect
                    onSelect={onFormChangeHandler}
                    value={defaultLocaleVal}
                    placeholder={trans('SELECT_LANGUAGE_L')}
                    options={
                      providerData &&
                      providerData.supportedlocales
                        ?.map((item) => ({
                          value: item.slice(-6, -1),
                          label: item,
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label))
                    }
                  />
                </Form.Item>
              </div>
              <div className="edit-profile-form-row">
                <span className="edit-profile-form-row-title generalinfo">
                  {trans('MEASUREMENT_UNIT')}
                </span>
                <Form.Item name="units" className="edit-profile-form-row-input-box">
                  <HSSelect
                    onSelect={onFormChangeHandler}
                    dropdownStyle={{ padding: 1 }}
                    options={Units.map((unit) => ({
                      label: `${unit.label}`,
                      value: `${unit.value}`,
                      placeholder: trans('SELECT_MEASUREMENTUNIT_L'),
                    }))}
                  />
                </Form.Item>
              </div>
            </div>
            <div className="edit-profile-content-container">
              <div className="edit-profile-form-row">
                <div>
                  <span className="edit-profile-form-row-title generalinfo">
                    {trans('POSTALCODE')}
                    <span className="optional-label">({trans('OPTIONAL')})</span>
                  </span>
                </div>
                <Form.Item name={'postalCode'} className="edit-profile-form-row-input-box">
                  <Input ref={postalCodeRef} placeholder={trans('POSTAL_CODE_L')} />
                </Form.Item>
                <div className="postal-code-info-container-new">
                  <span className="postal-code-info-message-new">
                    <ErrorIcon className="error-icon" fillColor="#3FA9F5" />
                    {trans('WENEEDTHISINFORMATIONTOPROVIDEYOUWITHTHEWEATHERFORECAST')}
                  </span>
                </div>
              </div>

              <div style={{ marginTop: showWarningmessage ? '2rem' : '0.9rem' }}>
                <div className="edit-profile-form-row">
                  <span className="edit-profile-form-row-title generalinfo">{trans('TIME_')}</span>
                  <Form.Item
                    name="timeZone"
                    className={`edit-profile-form-row-input-box ${
                      showWarningmessage ? 'timezone-error-field' : ''
                    }`}
                    validateStatus={showWarningmessage ? 'error' : ''}
                    help={
                      showWarningmessage ? (
                        <div className="error-text">
                          <span className="error-message">
                            <ErrorIcon className="error-icon" fillColor="#F46363" />
                            <span>{trans('PLEASEUPDATETHETIMEZONESELECTION')}</span>
                          </span>
                        </div>
                      ) : null
                    }
                  >
                    <HSSelect
                      className={`form-item-dropdown custom-select ${
                        showWarningmessage ? 'error-select' : ''
                      }`}
                      onSelect={() => {
                        onFormChangeHandler();
                        setShowWarningMessage(false); // Hide warning when user selects a time zone
                      }}
                      dropdownStyle={{ padding: 1 }}
                      options={getTimezoneOptions()}
                    />
                  </Form.Item>
                </div>
                <div className="edit-profile-form-row">
                  <Form.Item name={'is24hoursformat'} className="edit-profile-form-row-input-box">
                    <div className="togglebutton">
                      <div>
                        <Switch
                          checked={isTimeFormatEnabled}
                          onChange={(checked) => {
                            setIsTimeFormatEnabled(checked);
                            setAreButtonsDisabled(
                              !form.getFieldValue('timeZone') ||
                                (checked === userInfo?.is24hoursformat &&
                                  allow === initialAllow &&
                                  accountPrincipal === initialAccountPrincipal &&
                                  manage === initialManage &&
                                  care === initialCare &&
                                  social === initialSocial &&
                                  autoCallAllow === initialAutoCallAllow &&
                                  autoCallManage === initialAutoCallManage &&
                                  autoCallCare === initialAutoCallCare &&
                                  autoCallSocial === initialAutoCallSocial &&
                                  Object.keys(formData).every(
                                    (key) => form.getFieldValue(key) === formData[key]
                                  ))
                            );
                          }}
                        />
                      </div>
                      <div className="timeformat-text">{trans('24HOURFORMAT_L')}</div>
                    </div>
                  </Form.Item>
                </div>
              </div>
            </div>
          </>
        )}

        {isLoggedInUser && (
          <>
            <Divider className="edit-profile-divider" />
            <div className="edit-profile-content-container">
              {isStaffRolePresent && (
                <div className="edit-profile-form-row">
                  <div>
                    <span className="edit-profile-form-row-title">
                      {trans('STAFFROLE')}
                      <span className="optional-label">({trans('OPTIONAL')})</span>
                    </span>
                  </div>
                  <Form.Item name={'staffRole'} className="edit-profile-form-row-input-box">
                    <Input placeholder={trans('STAFFROLE')} />
                  </Form.Item>
                </div>
              )}
              <div
                className={`${
                  isStaffRolePresent ? 'edit-profile-form-row emailcenter' : 'edit-profile-form-row'
                } `}
              >
                {formData.email && (
                  <>
                    <span className="edit-profile-form-row-title">{trans('EMAIL')}</span>
                    <Form.Item name={'email'} className="edit-profile-form-row-input-box">
                      <span>{formData.email}</span>
                    </Form.Item>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </>
    );
  };

  const renderPrincipalUserSettingsSection = () => {
    return (
      <>
        {(hubRecord?.rolename !== undefined || showContactingStaff) && (
          <>
            <span className="edit-profile-general-info-label">{trans('SETTINGS')}</span>
            <div className="edit-profile-content-container">
              <div className="edit-profile-authorization-form">
                <div className="edit-profile-form-column">
                  {hasStaffPrivileges(currentUser?.permission_level) && (
                    <>
                      <span className="edit-profile-form-row-title deployment">
                        {trans('ACCOUNTTYPE')}
                      </span>
                      <Form.Item name="deployment_state">
                        <HSSelect
                          className="edit-profile-form-row-input-box account-type-dropdown"
                          dropdownClassName="account-type-dropdown-menu"
                          value={isDeployed ? 'DEPLOYED' : 'NOT_DEPLOYED'}
                          onChange={(value) => {
                            setIsDeployed(value === 'DEPLOYED');
                          }}
                          options={[
                            { value: 'DEPLOYED', label: trans('CLIENT') },
                            { value: 'NOT_DEPLOYED', label: trans('DEMO') },
                          ]}
                          placeholder={trans('SELECT_ACCOUNT_TYPE')}
                        />
                      </Form.Item>
                      <span className="edit-profile-form-row-title contactingstaff">
                        {trans('CONTACTINGSTAFF')}
                      </span>
                      <Form.Item name="staffcontact">
                        <div className="contactingstaff">
                          <div className="togglebutton">
                            <div className="subtogglebuttonprincipal">
                              <Switch
                                checked={allow}
                                onChange={(checked) => {
                                  setAllow(checked);
                                  setAccountPrincipal(checked);
                                  setManage(checked);
                                  setCare(checked);
                                  setSocial(checked);
                                  onFormChangeHandler(
                                    !form.getFieldValue('timeZone') ||
                                      (checked === initialAllow &&
                                        Object.keys(formData).every(
                                          (key) => form.getFieldValue(key) === formData[key]
                                        ) &&
                                        isTimeFormatEnabled === userInfo?.is24hoursformat &&
                                        autoCallAllow === initialAutoCallAllow &&
                                        accountPrincipal === initialAccountPrincipal &&
                                        manage === initialManage &&
                                        care === initialCare &&
                                        social === initialSocial &&
                                        toggleUpdateOnToggleAllow &&
                                        allowedPermissions === (checked ? 4 : 0))
                                  );
                                }}
                              />
                            </div>
                            <div className="timeformat-text">{trans('ALLOW')}</div>
                          </div>
                          <div className="subtogglelist">
                            <div className="subtogglebutton">
                              <div className="subtogglebuttonprincipal">
                                <Switch
                                  checked={accountPrincipal}
                                  onChange={(checked) => {
                                    setAccountPrincipal(checked);
                                    onFormChangeHandler(
                                      !form.getFieldValue('timeZone') ||
                                        (checked === initialAccountPrincipal &&
                                          manage === initialManage &&
                                          care === initialCare &&
                                          social === initialSocial &&
                                          allow === initialAllow &&
                                          autoCallAllow === initialAutoCallAllow &&
                                          accountPrincipal === initialAccountPrincipal &&
                                          manage === initialManage &&
                                          care === initialCare &&
                                          social === initialSocial &&
                                          isTimeFormatEnabled === userInfo?.is24hoursformat &&
                                          Object.keys(formData).every(
                                            (key) => form.getFieldValue(key) === formData[key]
                                          ))
                                    );
                                  }}
                                  disabled={!allow}
                                />
                              </div>
                              <div className="timeformat-text">{trans('ACCOUNTPRINCIPAL')}</div>
                            </div>
                            <div className="subtogglebutton">
                              <div className="subtogglebuttonprincipal">
                                <Switch
                                  checked={manage}
                                  onChange={(checked) => {
                                    setManage(checked);
                                    onFormChangeHandler(
                                      !form.getFieldValue('timeZone') ||
                                        (checked === initialManage &&
                                          accountPrincipal === initialAccountPrincipal &&
                                          care === initialCare &&
                                          social === initialSocial &&
                                          allow === initialAllow &&
                                          autoCallAllow === initialAutoCallAllow &&
                                          manage === initialManage &&
                                          care === initialCare &&
                                          social === initialSocial &&
                                          isTimeFormatEnabled === userInfo?.is24hoursformat &&
                                          Object.keys(formData).every(
                                            (key) => form.getFieldValue(key) === formData[key]
                                          ))
                                    );
                                  }}
                                  disabled={!allow}
                                />
                              </div>
                              <div className="timeformat-text">{trans('MANAGE')}</div>
                            </div>
                            <div className="subtogglebutton">
                              <div className="subtogglebuttonprincipal">
                                <Switch
                                  checked={care}
                                  onChange={(checked) => {
                                    setCare(checked);
                                    onFormChangeHandler(
                                      !form.getFieldValue('timeZone') ||
                                        (checked === initialCare &&
                                          accountPrincipal === initialAccountPrincipal &&
                                          manage === initialManage &&
                                          social === initialSocial &&
                                          allow === initialAllow &&
                                          autoCallAllow === initialAutoCallAllow &&
                                          care === initialCare &&
                                          Object.keys(formData).every(
                                            (key) => form.getFieldValue(key) === formData[key]
                                          ) &&
                                          isTimeFormatEnabled === userInfo?.is24hoursformat)
                                    );
                                  }}
                                  disabled={!allow}
                                />
                              </div>
                              <div className="timeformat-text">{trans('CARE')}</div>
                            </div>
                            <div className="subtogglebutton">
                              <div className="subtogglebuttonprincipal">
                                <Switch
                                  checked={social}
                                  onChange={(checked) => {
                                    setSocial(checked);
                                    onFormChangeHandler(
                                      !form.getFieldValue('timeZone') ||
                                        (checked === initialSocial &&
                                          accountPrincipal === initialAccountPrincipal &&
                                          manage === initialManage &&
                                          care === initialCare &&
                                          allow === initialAllow &&
                                          autoCallAllow === initialAutoCallAllow &&
                                          social === initialSocial &&
                                          Object.keys(formData).every(
                                            (key) => form.getFieldValue(key) === formData[key]
                                          ) &&
                                          isTimeFormatEnabled === userInfo?.is24hoursformat)
                                    );
                                  }}
                                  disabled={!allow}
                                />
                              </div>
                              <div className="timeformat-text">{trans('SOCIAL')}</div>
                            </div>
                          </div>
                        </div>
                      </Form.Item>
                    </>
                  )}
                  <span className="edit-profile-form-row-title-screen-saver">
                    {trans('SCREEN_SAVER_L')}
                  </span>
                  <Form.Item name="screen-saver-block">
                    <div className="screen-saver-text-to-speech-container">
                      <div className="screen-saver-block-main-container">
                        <div className="screen-saver-allow-block">
                          <div className="screen-saver-allow-block-switch-container">
                            <Switch
                              checked={allowScreenSaver}
                              onChange={(val) => setAllowScreenSaver(val)}
                            />
                          </div>
                          <span
                            className={`screen-saver-allow-text ${
                              allowScreenSaver ? 'screen-saver-allow-text-label-checked' : ''
                            }`}
                          >
                            {trans('ALLOW')}
                          </span>
                        </div>
                        <div className="screen-saver-content-input-block">
                          <HSSelect
                            style={{ width: 280 }}
                            value={getScreenSaverDelayValue()}
                            onChange={handleScreenSaverChange}
                            options={toSelectOptions(screenSaverMenuItems)}
                            disabled={!allowScreenSaver}
                            aria-label={trans('SCREEN_SAVER_L')}
                          />
                        </div>
                      </div>
                    </div>
                  </Form.Item>
                </div>

                {/* NEW auto-call toggles (for autoacceptcall section) */}
                <div className="edit-profile-form-column">
                  <span className="edit-profile-form-row-title contactingstaff">
                    {trans('CALLSETTINGS')}
                  </span>
                  <Form.Item name="allowautocallroles">
                    <div className="contactingstaff">
                      <div className="togglebutton">
                        <div className="subtogglebuttonprincipal">
                          <Switch
                            checked={autoCallAllow}
                            onChange={(checked) => {
                              setAutoCallAllow(checked);
                              setAutoCallManage(checked);
                              setAutoCallCare(checked);
                              setAutoCallSocial(checked);
                              onFormChangeHandler(
                                !form.getFieldValue('timeZone') ||
                                  (checked === initialAutoCallAllow &&
                                    autoCallManage === initialAutoCallManage &&
                                    autoCallCare === initialAutoCallCare &&
                                    autoCallSocial === initialAutoCallSocial &&
                                    allow === initialAllow &&
                                    manage === initialManage &&
                                    care === initialCare &&
                                    social === initialSocial &&
                                    isTimeFormatEnabled === userInfo?.is24hoursformat &&
                                    Object.keys(formData).every(
                                      (key) => form.getFieldValue(key) === formData[key]
                                    ))
                              );
                            }}
                          />
                        </div>
                        <div className="timeformat-text">{trans('ALLOWAUTOANSWER')}</div>
                      </div>
                      <div className="subtogglelist">
                        <div className="subtogglebutton">
                          <div className="subtogglebuttonprincipal">
                            <Switch
                              checked={autoCallManage}
                              onChange={(checked) => {
                                setAutoCallManage(checked);
                                // onFormChangeHandler(
                                //   !form.getFieldValue("timeZone") ||
                                //     (checked === initialAutoCallManage &&
                                //       autoCallCare === initialAutoCallCare &&
                                //       autoCallSocial ===
                                //         initialAutoCallSocial &&
                                //       autoCallAllow === initialAutoCallAllow &&
                                //       allow === initialAllow  &&
                                //       accountPrincipal ===
                                //         initialAccountPrincipal &&
                                //       manage === initialManage &&
                                //       care === initialCare &&
                                //       social === initialSocial &&
                                //       isTimeFormatEnabled ===
                                //         userInfo?.is24hoursformat &&
                                //       Object.keys(formData).every(
                                //         (key) =>
                                //           form.getFieldValue(key) ===
                                //           formData[key]
                                //       ))
                                //  );
                              }}
                              disabled={!autoCallAllow}
                            />
                          </div>
                          <div className="timeformat-text">{trans('MANAGE')}</div>
                        </div>
                        <div className="subtogglebutton">
                          <div className="subtogglebuttonprincipal">
                            <Switch
                              checked={autoCallCare}
                              onChange={(checked) => {
                                setAutoCallCare(checked);
                                // onFormChangeHandler(
                                //   !form.getFieldValue("timeZone") ||
                                //     (checked === initialAutoCallCare &&
                                //       autoCallManage ===
                                //         initialAutoCallManage &&
                                //       autoCallSocial ===
                                //         initialAutoCallSocial &&
                                //       autoCallAllow === initialAutoCallAllow &&
                                //       allow === initialAllow  &&
                                //       accountPrincipal ===
                                //         initialAccountPrincipal &&
                                //       manage === initialManage &&
                                //       care === initialCare &&
                                //       social === initialSocial &&
                                //       isTimeFormatEnabled ===
                                //         userInfo?.is24hoursformat &&
                                //       Object.keys(formData).every(
                                //         (key) =>
                                //           form.getFieldValue(key) ===
                                //           formData[key]
                                //       ))
                                // );
                              }}
                              disabled={!autoCallAllow}
                            />
                          </div>
                          <div className="timeformat-text">{trans('CARE')}</div>
                        </div>
                        <div className="subtogglebutton">
                          <div className="subtogglebuttonprincipal">
                            <Switch
                              checked={autoCallSocial}
                              onChange={(checked) => {
                                setAutoCallSocial(checked);
                                // onFormChangeHandler(
                                //   !form.getFieldValue("timeZone") ||
                                //     (checked === initialAutoCallSocial &&
                                //       autoCallManage ===
                                //         initialAutoCallManage &&
                                //       autoCallCare === initialAutoCallCare &&
                                //       autoCallAllow === initialAutoCallAllow &&
                                //       allow === initialAllow  &&
                                //       accountPrincipal ===
                                //         initialAccountPrincipal &&
                                //       manage === initialManage &&
                                //       care === initialCare &&
                                //       social === initialSocial &&
                                //       isTimeFormatEnabled ===
                                //         userInfo?.is24hoursformat &&
                                //       Object.keys(formData).every(
                                //         (key) =>
                                //           form.getFieldValue(key) ===
                                //           formData[key]
                                //       ))
                                // );
                              }}
                              disabled={!autoCallAllow}
                            />
                          </div>
                          <div className="timeformat-text">{trans('SOCIAL')}</div>
                        </div>
                      </div>
                    </div>
                    <div className="edit-profile-toggle-buttons-container">
                      <div className="edit-profile-toggle-button-row">
                        <Switch
                          checked={showVideoControls}
                          onChange={(val) => setShowVideoControls(val)}
                        />
                        <span
                          className={`edit-profile-toggle-button-row-label ${
                            showVideoControls ? 'edit-profile-toggle-button-row-label-checked' : ''
                          }`}
                        >
                          {trans('SHOWTVCCONTROLS')}
                        </span>
                      </div>
                      <div className="edit-profile-toggle-button-row">
                        <Switch checked={audioCallOnly} onChange={(val) => setAudioCallOnly(val)} />
                        <span
                          className={`edit-profile-toggle-button-row-label ${
                            audioCallOnly ? 'edit-profile-toggle-button-row-label-checked' : ''
                          }`}
                        >
                          {trans('AUDIOCALLONLY')}
                        </span>
                      </div>
                    </div>
                  </Form.Item>
                </div>

                <div className="edit-profile-form-column">
                  <div className="screen-saver-text-to-speech-container">
                    <div className="notification-timeout-block-main-container">
                      <span className="edit-profile-form-row-title-notification-timeout">
                        {trans('TVNOTIFICATIONS')}
                      </span>
                      <div className="notification-timeout-content-input-block">
                        <span className="edit-profile-dropdown-label">{trans('AUTODISMISS')}</span>
                        <HSSelect
                          style={{ width: 280 }}
                          value={getNotificationTimeoutValue()}
                          onChange={handleNotificationTimeoutChange}
                          options={toSelectOptions(notificationTimeoutMenuItems)}
                          aria-label={trans('AUTODISMISS')}
                        />
                      </div>
                    </div>
                    <Form.Item name="notification-volume-text-to-speech">
                      <Tooltip
                        title={trans(
                          'SETDESIREDVOLUMELEVELFORTHESMARTCAMERASPEAKERFORINCOMINGCALLSANDALLNOTIFICATIONSSET'
                        )}
                      >
                        <div className="edit-profile-notification-vol-container">
                          <span className="edit-profile-notification-vol-label">
                            {trans('NOTIFICATIONVOLUME')}
                          </span>
                          <Slider
                            className="edit-profile-notification-vol-progress-bar"
                            min={0}
                            max={10}
                            step={1}
                            value={volumeLevel}
                            onChange={(val) => {
                              setVolumeLevel(val);
                            }}
                          />
                          <div className="edit-profile-volume-level-container">
                            <span className="edit-profile-audio-level-value-label">
                              {trans('MUTE')}
                            </span>
                            <span className="edit-profile-volume-number">{volumeLevel}</span>
                            <span className="edit-profile-audio-level-value-label">
                              {trans('MAX')}
                            </span>
                          </div>
                        </div>
                      </Tooltip>
                      <div className="text-to-speech-main-container">
                        <div className="text-to-speech-main-block">
                          <div className="text-to-speech-content-container">
                            <div className="text-to-speech-allow-block-switch-container">
                              <Switch
                                checked={allowTextToSpeech}
                                onChange={(val) => setAllowTextToSpeech(val)}
                              />
                            </div>
                            <span
                              className={`text-to-speech-allow-text ${
                                allowTextToSpeech ? 'text-to-speech-allow-text-label-checked' : ''
                              }`}
                            >
                              {trans('VOICEANNOUNCEMENT')}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="edit-profile-toggle-buttons-container">
                        <Tooltip title={trans('AUTO_SWITCH_TOOLTIP')}>
                          <div className="edit-profile-toggle-button-row">
                            <Switch
                              checked={autoSwitchNtf}
                              onChange={(val) => setAutoSwitchNtf(val)}
                            />
                            <span
                              className={`edit-profile-toggle-button-row-label ${
                                autoSwitchNtf ? 'edit-profile-toggle-button-row-label-checked' : ''
                              }`}
                            >
                              {trans('TV_AUTO_WAKEUP_SWITCH')}
                            </span>
                          </div>
                        </Tooltip>
                        {/* <Tooltip
                          title={trans(
                            'TVWILLAUTOMATICALLYWAKEUPFORINCOMINGCALLS(FORCAPABLETVSWITHCECENABLED)'
                          )}
                        >
                          <div className="edit-profile-toggle-button-row">
                            <Switch checked={autoWakeup} onChange={(val) => setAutoWakeup(val)} />
                            <span
                              className={`edit-profile-toggle-button-row-label ${
                                autoWakeup ? 'edit-profile-toggle-button-row-label-checked' : ''
                              }`}
                            >
                              {trans('AUTOTVWAKEUP')}
                            </span>
                          </div>
                        </Tooltip> */}
                      </div>
                    </Form.Item>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </>
    );
  };

  const renderNormalUserSettingsTab = () => {
    return (
      <div className="edit-profile-settings-section">
        <span className="settings-notifications-title">{trans('NOTIFICATIONS')}</span>

        <div className="settings-alerts-panel">
          <div className="settings-alerts-header">
            <div className="settings-alert-key-column" />
            <div className="settings-alert-value-column">{trans('ALERTS')}</div>
          </div>

          <div className="settings-alert-row">
            <span className="settings-alert-label settings-alert-key-column">
              {trans('DESKTOP')}
            </span>
            <div className="settings-alert-value-column">
              <Switch
                checked={notificationAlertSettings.desktop}
                onChange={() => toggleNotificationAlertSetting('desktop')}
              />
            </div>
          </div>

          <div className="settings-alert-row">
            <span className="settings-alert-label settings-alert-key-column">
              {trans('MOBILE')}
            </span>
            <div className="settings-alert-value-column">
              <Switch
                checked={notificationAlertSettings.mobile}
                onChange={() => toggleNotificationAlertSetting('mobile')}
              />
            </div>
          </div>

          <div className="settings-alert-row">
            <span className="settings-alert-label settings-alert-key-column">{trans('EMAIL')}</span>
            <div className="settings-alert-value-column">
              <Switch
                checked={notificationAlertSettings.email}
                onChange={() => toggleNotificationAlertSetting('email')}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderChannelsTab = () => {
    return (
      <AppRegistryList
        ref={appRegistryListRef}
        useruuid={useruuid}
        launcherPlacement={APP_REGISTRY_LAUNCHER_PLACEMENT.CONTENT}
        canManageApps={canManageApps}
      />
    );
  };

  const renderAppsTab = () => {
    return (
      <AppRegistryList
        ref={appRegistryListRef}
        useruuid={useruuid}
        launcherPlacement={APP_REGISTRY_LAUNCHER_PLACEMENT.APPS}
        canManageApps={canManageApps}
      />
    );
  };

  const renderProfileDetails = () => {
    return (
      <Form
        form={form}
        initialValues={formData}
        onChange={onFormChangeHandler}
        colon={false}
        ref={formRef}
        onValuesChange={(changedValues) => {
          if (changedValues.hasOwnProperty('locale')) {
            const value = formRef.current ? formRef.current.getFieldValue('locale') : null;
            setLocale(value);
            // Reset timeZone if locale changes
            form.setFieldsValue({ timeZone: '' });
            setAreButtonsDisabled(true);
            setShowWarningMessage(true); // Show warning message
          }
        }}
      >
        <Tabs
          activeKey={activeUserInfo}
          onChange={changeTabForUserInfo}
          className="staffaccountaccess-userprofile-tabs"
          destroyOnHidden
        >
          <TabPane tab={trans('GENERAL')} key="1">
            <div
              className={
                hubRecord?.rolename !== undefined || showContactingStaff
                  ? 'edit-profile-info-container'
                  : 'edit-profile-info-container-new'
              }
            >
              {renderGeneralTab()}
              {renderMandatoryTextAndButtons()}
            </div>
          </TabPane>
          <TabPane tab={trans('SETTINGS')} key="2">
            <div className="edit-profile-info-container settings-tab-content">
              {isLoggedInUser
                ? renderNormalUserSettingsTab()
                : renderPrincipalUserSettingsSection()}
              {renderMandatoryTextAndButtons({ showMandatoryLabel: false })}
            </div>
          </TabPane>
          {isPrincipalUser && (
            <TabPane tab={trans('CHANNELS')} key="3">
              <div
                className={
                  hubRecord?.rolename !== undefined || showContactingStaff
                    ? 'edit-profile-info-container'
                    : 'edit-profile-info-container-new'
                }
              >
                {renderChannelsTab()}
              </div>
            </TabPane>
          )}
          {isPrincipalUser && (
            <TabPane tab={trans('APPS')} key="4">
              <div
                className={
                  hubRecord?.rolename !== undefined || showContactingStaff
                    ? 'edit-profile-info-container'
                    : 'edit-profile-info-container-new'
                }
              >
                {renderAppsTab()}
              </div>
            </TabPane>
          )}
        </Tabs>
      </Form>
    );
  };

  const handleHSModalOk = () => {
    if (hsModalDetails.type === MODAL_TYPE.DID_YOU_KNOW) {
      setHSModalDetails({ ...hsModalDetails, open: false });
      if (postalCodeRef.current) {
        postalCodeRef.current.focus();
      }
    } else if (hsModalDetails.type === MODAL_TYPE.UNSAVED_APP_CHANGES) {
      // Escape/mask-dismiss defaults to "stay put", never an implicit save+switch/navigate.
      setPendingUnsavedAction(null);
      setHSModalDetails({ ...hsModalDetails, open: false });
    }
  };

  const handleHSModalCancel = () => {
    if (hsModalDetails.type === MODAL_TYPE.DID_YOU_KNOW) {
      submitForm();
      setHSModalDetails({ ...hsModalDetails, open: false });
    } else if (hsModalDetails.type === MODAL_TYPE.UNSAVED_APP_CHANGES) {
      setPendingUnsavedAction(null);
      setHSModalDetails({ ...hsModalDetails, open: false });
    }
  };

  // "Cancel" in the unsaved-changes dialog: dismiss it, stay put, keep the staged
  // channel/app toggle changes untouched.
  const handleUnsavedAppChangesCancel = () => {
    setPendingUnsavedAction(null);
    setHSModalDetails({ ...hsModalDetails, open: false });
  };

  // "Update" in the unsaved-changes dialog: bulk-save the staged toggle changes, then
  // complete whatever navigation/tab-switch the user originally attempted.
  const handleUnsavedAppChangesConfirm = () => {
    appRegistryListRef.current
      ?.save?.()
      .then(() => {
        pendingUnsavedAction?.();
        setPendingUnsavedAction(null);
        setHSModalDetails({ ...hsModalDetails, open: false });
      })
      .catch(() => {
        // Save failed - leave the dialog open and the toggle changes staged so the user can retry.
      });
  };

  return (
    <MainPage
      header={header}
      hasSider={path.includes('profile') ? true : false}
      buttonSelected="gear"
      noModals={!!openSubMenu}
    >
      <div
        className={`edit-profile-container ${
          isLoggedInUser ? 'edit-user-profile-container-overflow' : ''
        } ${
          canShowAuthSettings
            ? 'edit-profile-auth-settings-padding'
            : 'edit-profile-default-padding'
        }`}
      >
        {fetchingUserInfo ? (
          <Loading message={trans('FETCHINGUSERPROFILE')} />
        ) : isUpdatingProfile ? (
          <Loading message={trans('UPDATINGUSERPROFILE')} />
        ) : (
          <Spin size="large" spinning={processing} tip={trans('PROCESSING')}>
            {renderHeaderSection()}
            <CustomDialogBox
              isModalOpen={Boolean(dialogConfig)}
              modalHeader={dialogConfig?.title || ''}
              description={dialogConfig?.description || null}
              showDeleteDescrPath={dialogConfig?.showDeleteDescrPath || false}
              userFullName={dialogConfig?.userFullName || ''}
              confirmLabel={dialogConfig?.confirmLabel || ''}
              cancelLabel={dialogConfig?.cancelLabel || ''}
              confirmIcon={dialogConfig?.confirmIcon}
              hideCancel={dialogConfig?.hideCancel || false}
              onConfirm={() => {
                dialogConfig?.onConfirm?.();
                closeDialog();
              }}
              onCancel={() => {
                dialogConfig?.onCancel?.();
                closeDialog();
              }}
            />
            {isLoggedInUser && (
              <div className="custom-button-container">
                <NewCustomButton
                  label={trans('CHANGEPASSWORD')}
                  className={'password-button'}
                  onClick={onChangePasswordHandler}
                />
              </div>
            )}
            <div
              className={`edit-profile-form-container ${
                canShowAuthSettings ? 'edit-profile-form-container-auth-block' : ''
              }`}
            >
              <div>{renderProfileDetails()}</div>
              <HSModal
                open={hsModalDetails.open}
                handleOk={handleHSModalOk}
                title={hsModalDetails.title}
                handleCancel={handleHSModalCancel}
                content={hsModalDetails.content}
                footer={
                  hsModalDetails.type === MODAL_TYPE.DID_YOU_KNOW
                    ? [
                        <NewCustomButton
                          key={1}
                          className="cancel"
                          onClick={handleHSModalCancel}
                          label={trans('MAYBELATER')}
                          icon={<NewCloseIcon className="cancel-icon" />}
                        />,
                        <NewCustomButton
                          key={2}
                          className="confirm"
                          type={ButtonTypes.primary}
                          label={trans('LETSFILLIT')}
                          icon={<NewUpdateIcon className="update-icon" />}
                          onClick={handleHSModalOk}
                        />,
                      ]
                    : hsModalDetails.type === MODAL_TYPE.UNSAVED_APP_CHANGES
                      ? [
                          <NewCustomButton
                            key={1}
                            className="cancel"
                            onClick={handleUnsavedAppChangesCancel}
                            label={trans('CANCEL')}
                            icon={<NewCloseIcon className="cancel-icon" />}
                          />,
                          <NewCustomButton
                            key={2}
                            className="confirm"
                            type={ButtonTypes.primary}
                            label={trans('UPDATE')}
                            icon={<NewUpdateIcon className="update-icon" />}
                            onClick={handleUnsavedAppChangesConfirm}
                          />,
                        ]
                      : null
                }
              />
            </div>
          </Spin>
        )}
      </div>
    </MainPage>
  );
};

EditUserProfile.propTypes = {
  parentBread: PropTypes.arrayOf(PropTypes.object),
  hubRecord: PropTypes.object,
  fetchUserHubs: PropTypes.func,
  onActionClick: PropTypes.func,
  handleTabChange: PropTypes.func,
  userHubCall: PropTypes.bool,
  openSubMenu: PropTypes.bool,
  showContactingStaff: PropTypes.bool,
  filteredUserHubsData: PropTypes.array,
  successCallBack: PropTypes.func,
  parentActionClick: PropTypes.func,
  showStaffAccessBlocks: PropTypes.bool,
};

export default EditUserProfile;
